import { TranslationKey } from '../../../../../../types/translation/TranslationKey';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ProductMasterData } from '../../../../../../types/product_master_data/productMasterData';
import { Grid } from '@mui/material';
import { t } from '../../../../../../types/translation/Translator';
import { useNavigate } from 'react-router-dom';
import { LotContext } from '../../../../../../context/LotContext';
import { ProductTransaction, ProductTransactionParentType } from '../../../../../../types/productTransaction';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { UserContext } from '../../../../../../context/UserContext';
import { iconForType } from '../../../../../../util/productTransactions.util';
import ProductTransactionDetailModal from '../../Modals/ProductTransactionDetailModal';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { testIds } from '../../../../../../util/identifiers/identifiers.util';
import Table from '../../../../../components/Table';
import FlexPane from '../../../../../../VentoryUI/components/common/FlexPane';
import SearchBarWithFilter from '../../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ProductTransactionFilter } from '../../../../../../VentoryUI/components/filters/ProductTransactionFilter';
import { OrderContext } from '../../../../../../context/OrderContext';
import { TagRelationContext } from '../../../../../../context/TagRelationContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { DateTimeUtil } from '../../../../../../util/date.util';
import { CompanyContext } from '../../../../../../context/CompanyContext';

interface ProductHistoryPaneInputProps {
  productMasterData?: ProductMasterData;
  footer: () => JSX.Element;
}

export default function ProductHistoryPane({ productMasterData: pmd, footer }: ProductHistoryPaneInputProps) {
  const navigate = useNavigate();

  const { lots } = useContext(LotContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { productTransactions } = useContext(ProductTransactionContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { containers } = useContext(ContainerContext);
  const { orders } = useContext(OrderContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { currentCompany } = useContext(CompanyContext);

  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<ProductTransaction | null>(null);

  if (!pmd) return null; // TODO: Entity not found

  const items = useMemo(() => {
    return [...productTransactions.values()]
      .filter(p => p.product.pmdId === pmd.id && p.parentType !== ProductTransactionParentType.transaction)
      .sort(
        (a, b) => new Date(b.processedAt || b.createdAt).getTime() - new Date(a.processedAt || a.createdAt).getTime(),
      );
  }, [productTransactions]);

  const [filteredItems, setFilteredItems] = useState(items);

  const headers = [
    {
      key: 'processedAt',
      name: t(TranslationKey.processedAt).singular.label,
      text: (item: ProductTransaction) => {
        return DateTimeUtil.toLocaleDateTimeString(locale, new Date(item.processedAt || item.createdAt));
      },
      sortValue: (item: ProductTransaction) => item.processedAt || item.createdAt,
    },
    {
      key: 'processedBy',
      name: t(TranslationKey.processedBy).singular.label,
      text: (item: ProductTransaction) => {
        return item.metadata?.user?.email || '';
      },
    },
    {
      key: 'serialNumber',
      name: t(TranslationKey.serial).singular.label,
      text: (item: ProductTransaction) => item.product.serialNbr || '',
    },
    {
      key: 'lpn',
      name: t(TranslationKey.lpn).singular.label,
      text: (item: ProductTransaction) => item.product.lpn || '',
    },
    {
      key: 'lotNumber',
      name: t(TranslationKey.lotNumber).singular.label,
      text: (item: ProductTransaction) => lots.get(item.product.lotId || '')?.number || '',
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      sortValue: (item: ProductTransaction) => item.product.processedQuantity || item.product.quantity,
      text: (item: ProductTransaction) => {
        return (
          <Grid container columnSpacing={1} justifyContent={'flex-end'}>
            <Grid item marginY={'auto'} className='h-7'>
              <p
                title={item.product.quantity.toString()}
                className={`h-7 overflow-hidden text-ellipsis whitespace-nowrap py-1 text-sm`}
              >
                {UnitOfMeasure.quantityWithSuffix(
                  productMasterData.get(item.product.pmdId || '')?.unitOfMeasure,
                  (item.product.processedQuantity || item.product.quantity).toString(),
                )}
              </p>
            </Grid>
            <Grid item marginY={'auto'}>
              {iconForType(item)}
            </Grid>
          </Grid>
        );
      },
    },
  ].filter(item => {
    if (item.key === 'serialNumber' && !pmd.serialManaged) return false;
    if (item.key === 'lotNumber' && !pmd.lotManaged) return false;

    return true;
  });

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  const filter = new ProductTransactionFilter(
    companyUsers,
    productMasterData,
    orders,
    tagRelations,
    containers,
    customFields,
    stockLocations,
    productTransactions,
    currentCompany,
  );

  return (
    <>
      {selectedTransaction ? (
        <ProductTransactionDetailModal
          open={openDetailModal}
          setOpen={v => {
            setOpenDetailModal(v);
            setSelectedTransaction(null);
          }}
          transaction={selectedTransaction}
        />
      ) : null}

      <FlexPane
        testId={testIds.productHistoryPane}
        header={
          <SearchBarWithFilter
            items={items}
            setItems={setFilteredItems}
            placeholder={t(TranslationKey.filterProductTransactions).singular.label}
            filter={filter}
          />
        }
        content={
          <Table
            items={filteredItems}
            headers={headers}
            totalItemCount={items.length}
            onClick={item => {
              setSelectedTransaction(item);
              setOpenDetailModal(true);
            }}
            filter={filter}
          />
        }
        footer={footer()}
      />
    </>
  );
}
